import { ReactElement, useMemo, useState } from 'react';
import { MenuIcon, MultiSelect, PriceBandIcon, SearchField } from '@weareopenr/component-library';
import SelectableListWithControls from 'src/components/SelectableListWithControls/SelectableListWithControls';
import { SelectableListOption } from 'src/components/SelectableList/SelectableList';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useMenuLabels } from 'src/hooks/useMenuLabels';
import { filterByMenuLabels, toMenuLabelFilterOptions } from 'src/utils/menuLabels/menuLabelFilters';
import { IMenu } from 'src/types/menu.types';
import styles from './MenuSetMenusStep.module.scss';

interface IMenuSetMenusStepProps {
  menus: IMenu[];
  selectedMenuIds: string[];
  onMenusChange: (menuIds: string[]) => void;
  isLoading: boolean;
}

const toOption = (menu: IMenu): SelectableListOption => ({
  id: menu.menuId,
  label: menu.name,
  subtitle: menu.ranges.map(({ name }) => name).join(', '),
});

export const MenuSetMenusStep = ({
  menus,
  selectedMenuIds,
  onMenusChange,
  isLoading,
}: IMenuSetMenusStepProps): ReactElement => {
  const [searchTerm, setSearchTerm] = useState('');
  const [rangeIds, setRangeIds] = useState<string[]>([]);
  const [menuLabelIds, setMenuLabelIds] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm);
  const { data: menuLabels } = useMenuLabels();
  const menuLabelOptions = useMemo(() => toMenuLabelFilterOptions(menuLabels), [menuLabels]);

  const rangeOptions = useMemo(() => {
    const ranges = new Map<string, string>(
      menus.flatMap(({ ranges }) => ranges.map(({ range_id, name }): [string, string] => [range_id, name])),
    );
    return Array.from(ranges, ([id, label], index) => ({ id, label, display_order: index }));
  }, [menus]);

  const allOptions = useMemo(() => menus.map(toOption), [menus]);
  const options = useMemo(() => {
    const matchingMenuIds = new Set(
      filterByMenuLabels(menus, menuLabelIds)
        .filter(({ ranges }) => rangeIds.length === 0 || ranges.some(({ range_id }) => rangeIds.includes(range_id)))
        .map(({ menuId }) => menuId),
    );
    return allOptions.filter(
      ({ id, label }) =>
        (!debouncedSearchTerm || label.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) &&
        matchingMenuIds.has(id),
    );
  }, [menus, allOptions, debouncedSearchTerm, rangeIds, menuLabelIds]);
  const selectedOptions = useMemo(
    () => allOptions.filter(({ id }) => selectedMenuIds.includes(id)),
    [allOptions, selectedMenuIds],
  );
  const isFiltered = Boolean(debouncedSearchTerm) || rangeIds.length > 0 || menuLabelIds.length > 0;

  return (
    <SelectableListWithControls
      options={options}
      selectedOptionsIds={selectedMenuIds}
      onOptionChange={onMenusChange}
      scrollContainerClassName={styles['list']}
      collapsible={{ selectedOptionsData: selectedOptions, isPinned: true }}
      listDetails={{
        title: 'Menus:',
        emptyMessage: isFiltered ? 'No menus match your search.' : 'There are no menus available for this menu source.',
        isLoading,
      }}
    >
      <SearchField isDisabled={false} placeholder="Search for a menu" onChange={(e) => setSearchTerm(e.target.value)} />
      {menuLabelOptions.length > 0 && (
        <MultiSelect
          Icon={MenuIcon}
          name="menu-labels"
          label="Menu labels"
          className={styles['filter']}
          options={menuLabelOptions}
          selected={menuLabelIds}
          onChange={setMenuLabelIds}
        />
      )}
      {rangeOptions.length > 0 && (
        <MultiSelect
          Icon={PriceBandIcon}
          name="ranges"
          label="Ranges"
          className={styles['filter']}
          options={rangeOptions}
          selected={rangeIds}
          onChange={setRangeIds}
        />
      )}
    </SelectableListWithControls>
  );
};

export default MenuSetMenusStep;
